import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Tabs,
  Tab,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  AccountBalanceWallet,
  CreditCard,
  TrendingUp,
  SwapHoriz,
  People,
  Refresh,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import walletStatsService, { WalletOverview, WalletActivityRow, WalletTokensBreakdown, WalletUser, TokenUser, TopupRow, TopupsResponse, CardEnrollment } from '../../services/wallet-stats.service';
import { ReportPeriod } from '../../services/reports.service';

const COLORS = ['#6B2FA0', '#00C49F', '#FFBB28', '#FF8042', '#0088FE', '#82ca9d', '#ffc658'];
const CARD_COLORS: Record<string, string> = {
  VISA: '#1A1F71',
  MASTERCARD: '#EB001B',
  MAESTRO: '#007DBB',
  AMEX: '#2E77BC',
  Desconhecido: '#9E9E9E',
};

const periodOptions: { value: ReportPeriod; label: string }[] = [
  { value: 'today',   label: 'Hoje' },
  { value: 'week',    label: 'Últimos 7 dias' },
  { value: 'month',   label: 'Último mês' },
  { value: 'quarter', label: 'Último trimestre' },
  { value: 'year',    label: 'Último ano' },
  { value: 'all',     label: 'Todo o período' },
];

const fmt = (v: number) => `${Math.round(v || 0).toLocaleString('pt-PT')}$`;
const fmtN = (v: number) => (v || 0).toLocaleString('pt-PT');

interface KpiCardProps {
  title: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  color?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, sub, icon, color = '#6B2FA0' }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </Box>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}18`, color, display: 'flex' }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const WalletStats: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState<ReportPeriod>('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<WalletOverview | null>(null);
  const [activity, setActivity] = useState<WalletActivityRow[]>([]);
  const [tokens, setTokens] = useState<WalletTokensBreakdown | null>(null);

  // Utilizadores tab state
  const [users, setUsers] = useState<WalletUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(50);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersStatus, setUsersStatus] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);

  // Utilizadores com tokens tab state
  const [tokenUsers, setTokenUsers] = useState<TokenUser[]>([]);
  const [tokenUsersTotal, setTokenUsersTotal] = useState(0);
  const [tokenUsersPage, setTokenUsersPage] = useState(0);
  const [tokenUsersRowsPerPage, setTokenUsersRowsPerPage] = useState(50);
  const [tokenUsersSearch, setTokenUsersSearch] = useState('');
  const [tokenUsersLoading, setTokenUsersLoading] = useState(false);

  // Tab 6: registos de tokenização de cartões (tentativas e desfechos)
  const [enrollments, setEnrollments] = useState<CardEnrollment[]>([]);
  const [enrollPorEstado, setEnrollPorEstado] = useState<Record<string, number>>({});
  const [enrollTotal, setEnrollTotal] = useState(0);
  const [enrollPage, setEnrollPage] = useState(0);
  const [enrollRowsPerPage, setEnrollRowsPerPage] = useState(50);
  const [enrollSearch, setEnrollSearch] = useState('');
  const [enrollStatus, setEnrollStatus] = useState('');
  const [enrollLoading, setEnrollLoading] = useState(false);

  // Carregamentos tab state
  const [topups, setTopups] = useState<TopupRow[]>([]);
  const [topupsSummary, setTopupsSummary] = useState<TopupsResponse['summary'] | null>(null);
  const [topupsTotal, setTopupsTotal] = useState(0);
  const [topupsPage, setTopupsPage] = useState(0);
  const [topupsRowsPerPage, setTopupsRowsPerPage] = useState(50);
  const [topupsSearch, setTopupsSearch] = useState('');
  const [topupsLoading, setTopupsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, ac, tk] = await Promise.all([
        walletStatsService.getOverview(period),
        walletStatsService.getActivity(period),
        walletStatsService.getTokensBreakdown(period),
      ]);
      if (ov.success) setOverview(ov.data);
      if (ac.success) setActivity(ac.data);
      if (tk.success) setTokens(tk.data);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [period]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await walletStatsService.getUsers({
        page: usersPage + 1,
        limit: usersRowsPerPage,
        search: usersSearch || undefined,
        status: usersStatus || undefined,
      });
      if (res.success) {
        setUsers(res.data);
        setUsersTotal(res.pagination.total);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar utilizadores');
    } finally {
      setUsersLoading(false);
    }
  }, [usersPage, usersRowsPerPage, usersSearch, usersStatus]);

  const loadTokenUsers = useCallback(async () => {
    setTokenUsersLoading(true);
    try {
      const res = await walletStatsService.getTokenUsers({
        page: tokenUsersPage + 1,
        limit: tokenUsersRowsPerPage,
        search: tokenUsersSearch || undefined,
      });
      if (res.success) {
        setTokenUsers(res.data);
        setTokenUsersTotal(res.pagination.total);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar utilizadores com tokens');
    } finally {
      setTokenUsersLoading(false);
    }
  }, [tokenUsersPage, tokenUsersRowsPerPage, tokenUsersSearch]);

  const loadEnrollments = useCallback(async () => {
    setEnrollLoading(true);
    try {
      const res = await walletStatsService.getCardEnrollments({
        page: enrollPage + 1,
        limit: enrollRowsPerPage,
        search: enrollSearch || undefined,
        status: enrollStatus || undefined,
      });
      if (res.success) {
        setEnrollments(res.data);
        setEnrollPorEstado(res.porEstado || {});
        setEnrollTotal(res.pagination.total);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar registos de cartões');
    } finally {
      setEnrollLoading(false);
    }
  }, [enrollPage, enrollRowsPerPage, enrollSearch, enrollStatus]);

  const loadTopups = useCallback(async () => {
    setTopupsLoading(true);
    try {
      const res = await walletStatsService.getTopups({
        page: topupsPage + 1,
        limit: topupsRowsPerPage,
        search: topupsSearch || undefined,
        period,
      });
      if (res.success) {
        setTopups(res.data);
        setTopupsTotal(res.pagination.total);
        setTopupsSummary(res.summary);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Erro ao carregar carregamentos');
    } finally {
      setTopupsLoading(false);
    }
  }, [topupsPage, topupsRowsPerPage, topupsSearch, period]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 3) loadUsers(); }, [tab, loadUsers]);
  useEffect(() => { if (tab === 4) loadTokenUsers(); }, [tab, loadTokenUsers]);
  useEffect(() => { if (tab === 5) loadTopups(); }, [tab, loadTopups]);
  useEffect(() => { if (tab === 6) loadEnrollments(); }, [tab, loadEnrollments]);

  // ── Tab 0: Overview ──────────────────────────────────────────────────────
  const renderOverview = () => {
    if (!overview) return null;
    const p = overview.periodo;
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Wallets</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Total de Wallets" value={fmtN(overview.total_wallets)} sub={`${fmtN(overview.wallets_activas)} activas`} icon={<AccountBalanceWallet />} color="#6B2FA0" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Saldo em Circulação" value={fmt(overview.saldo_circulacao)} sub={`${fmt(overview.saldo_bonus_total)} em bónus`} icon={<TrendingUp />} color="#00C49F" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Total Carregado (all)" value={fmt(overview.total_carregado_all)} icon={<TrendingUp />} color="#0088FE" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Total Gasto (all)" value={fmt(overview.total_gasto_all)} icon={<SwapHoriz />} color="#FF8042" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Total Taxas (all)" value={fmt(overview.total_taxas_all)} icon={<TrendingUp />} color="#00C49F" />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Período seleccionado</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Carregamentos" value={fmt(p.montante_topups)} sub={`${fmtN(p.count_topups)} transacções`} icon={<TrendingUp />} color="#6B2FA0" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Taxas Cobradas" value={fmt(p.montante_taxas)} sub="no período" icon={<TrendingUp />} color="#00C49F" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Pagamentos c/ Wallet" value={fmt(p.montante_pagamentos)} sub={`${fmtN(p.count_pagamentos)} transacções`} icon={<SwapHoriz />} color="#0088FE" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Transferências" value={fmt(p.montante_transferencias)} sub={`${fmtN(p.count_transferencias)} transacções`} icon={<People />} color="#FFBB28" />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Tokens SISP</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Total Tokens" value={fmtN(overview.tokens.total)} sub={`${fmtN(overview.tokens.activos)} activos`} icon={<CreditCard />} color="#6B2FA0" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Novos no Período" value={fmtN(overview.tokens.novos_periodo)} icon={<CreditCard />} color="#00C49F" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Pedidos de Tokenização" value={fmtN(overview.pedidos_tokenizacao.total)} sub={`${fmtN(overview.pedidos_tokenizacao.completed)} concluídos`} icon={<CreditCard />} color="#0088FE" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiCard title="Saldo Bloqueado" value={fmt(overview.saldo_bloqueado_total)} icon={<AccountBalanceWallet />} color="#FF8042" />
          </Grid>
        </Grid>
      </Box>
    );
  };

  // ── Tab 1: Actividade ───────────────────────────────────────────────────
  const renderActivity = () => (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Carregamentos e Pagamentos</Typography>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3, p: 2 }}>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={activity} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="topup" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6B2FA0" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#6B2FA0" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="pagamento" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00C49F" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#00C49F" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 12 }} />
            <RechartsTooltip formatter={(v: any) => fmt(v)} />
            <Legend />
            <Area type="monotone" dataKey="montante_topup" name="Carregamentos" stroke="#6B2FA0" fill="url(#topup)" strokeWidth={2} />
            <Area type="monotone" dataKey="montante_pagamento" name="Pagamentos" stroke="#00C49F" fill="url(#pagamento)" strokeWidth={2} />
            <Area type="monotone" dataKey="montante_transferencia" name="Transferências" stroke="#FFBB28" fill="none" strokeWidth={2} strokeDasharray="4 2" />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Número de Transacções</Typography>
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={activity} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <RechartsTooltip />
            <Legend />
            <Bar dataKey="topups" name="Carregamentos" fill="#6B2FA0" radius={[4, 4, 0, 0]} />
            <Bar dataKey="pagamentos" name="Pagamentos" fill="#00C49F" radius={[4, 4, 0, 0]} />
            <Bar dataKey="transferencias" name="Transferências" fill="#FFBB28" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </Box>
  );

  // ── Tab 2: Tokens ────────────────────────────────────────────────────────
  const renderTokens = () => {
    if (!tokens) return null;
    const pieData = tokens.by_card_type.map(c => ({ name: c.name, value: c.total }));

    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Donut por tipo de cartão */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Tokens por Tipo de Cartão</Typography>
              {pieData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 4 }}>Sem dados</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                      {pieData.map((entry, i) => (
                        <Cell key={entry.name} fill={CARD_COLORS[entry.name] || COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, justifyContent: 'center' }}>
                {tokens.by_card_type.map((c, i) => (
                  <Chip key={c.name} size="small" label={`${c.name}: ${c.activos} activos / ${c.total} total`}
                    sx={{ bgcolor: (CARD_COLORS[c.name] || COLORS[i % COLORS.length]) + '22', fontSize: 11 }} />
                ))}
              </Box>
            </Card>
          </Grid>

          {/* Novos tokens ao longo do tempo */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Novos Tokens ao Longo do Tempo</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={tokens.new_over_time} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <RechartsTooltip />
                  <Bar dataKey="novos_tokens" name="Novos tokens" fill="#6B2FA0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
        </Grid>

        {/* Pedidos de tokenização */}
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Pedidos de Tokenização (Token Enrollment)</Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={tokens.req_over_time} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="completed" name="Concluídos" fill="#00C49F" radius={[4, 4, 0, 0]} stackId="a" />
              <Bar dataKey="pending" name="Pendentes" fill="#FFBB28" radius={[4, 4, 0, 0]} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Top utilizadores com tokens */}
        {tokens.top_users.length > 0 && (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Top Utilizadores com Tokens Activos</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Utilizador</TableCell>
                      <TableCell align="center">Nº de Tokens</TableCell>
                      <TableCell>Último Uso</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tokens.top_users.map((u) => (
                      <TableRow key={u.cliente_id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{u.nome || u.cliente_id}</Typography>
                          {u.email && <Typography variant="caption" color="text.secondary">{u.email}</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={u.num_tokens} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {u.ultimo_uso ? new Date(u.ultimo_uso).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  };

  // ── Tab 3: Utilizadores com Wallet ──────────────────────────────────────
  const renderUsers = () => {
    const nivelColor = (n: string) => {
      if (n === 'GOLD') return '#FFD700';
      if (n === 'SILVER') return '#9E9E9E';
      if (n === 'PREMIUM') return '#6B2FA0';
      return '#4CAF50';
    };
    return (
      <Box>
        {/* Filtros */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Pesquisar por nome, email ou username…"
            value={usersSearch}
            onChange={e => { setUsersSearch(e.target.value); setUsersPage(0); }}
            onKeyDown={e => { if (e.key === 'Enter') loadUsers(); }}
            sx={{ minWidth: 300 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            select size="small" value={usersStatus} onChange={e => { setUsersStatus(e.target.value); setUsersPage(0); }}
            sx={{ minWidth: 160 }} label="Estado wallet"
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="ACTIVE">Activa</MenuItem>
            <MenuItem value="BLOCKED">Bloqueada</MenuItem>
            <MenuItem value="SUSPENDED">Suspensa</MenuItem>
          </TextField>
        </Box>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Utilizador</TableCell>
                    <TableCell align="center">Nível</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell align="right">Bónus</TableCell>
                    <TableCell align="right">Total Carregado</TableCell>
                    <TableCell align="right">Total Gasto</TableCell>
                    <TableCell align="right">Transacções</TableCell>
                    <TableCell>Criada em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum utilizador encontrado
                      </TableCell>
                    </TableRow>
                  ) : users.map(u => (
                    <TableRow key={u.wallet_id} hover sx={{ opacity: u.wallet_status !== 'ACTIVE' ? 0.6 : 1 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{u.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={u.nivel}
                          size="small"
                          sx={{ bgcolor: nivelColor(u.nivel) + '22', color: nivelColor(u.nivel), fontWeight: 600, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={u.wallet_status}
                          size="small"
                          color={u.wallet_status === 'ACTIVE' ? 'success' : u.wallet_status === 'BLOCKED' ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color={u.saldo > 0 ? 'success.main' : 'text.secondary'}>
                          {fmt(u.saldo)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" color="text.secondary">{fmt(u.saldo_bonus)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{fmt(u.total_carregado)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{fmt(u.total_gasto)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={fmtN(u.total_transacoes)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {u.wallet_criada ? new Date(u.wallet_criada).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={usersTotal}
            page={usersPage}
            onPageChange={(_, p) => setUsersPage(p)}
            rowsPerPage={usersRowsPerPage}
            onRowsPerPageChange={e => { setUsersRowsPerPage(parseInt(e.target.value, 10)); setUsersPage(0); }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </Card>
      </Box>
    );
  };

  // ── Tab 4: Utilizadores com Tokens ─────────────────────────────────────
  const renderTokenUsers = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Pesquisar por nome, email ou username…"
          value={tokenUsersSearch}
          onChange={e => { setTokenUsersSearch(e.target.value); setTokenUsersPage(0); }}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') loadTokenUsers(); }}
          sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        {tokenUsersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Utilizador</TableCell>
                  <TableCell align="center">Tokens</TableCell>
                  <TableCell align="center">Activos</TableCell>
                  <TableCell>Tipos de Cartão</TableCell>
                  <TableCell>Primeiro Token</TableCell>
                  <TableCell>Último Uso</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokenUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhum utilizador com tokens encontrado
                    </TableCell>
                  </TableRow>
                ) : tokenUsers.map(u => (
                  <TableRow key={String(u.cliente_id)} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{u.nome}</Typography>
                      {u.email && <Typography variant="caption" color="text.secondary">{u.email}</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={u.num_tokens} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={u.num_activos}
                        size="small"
                        color={u.num_activos > 0 ? 'success' : 'default'}
                        variant={u.num_activos > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {u.tipos_cartao.map(t => (
                          <Chip
                            key={t}
                            label={t}
                            size="small"
                            sx={{ bgcolor: (CARD_COLORS[t] || '#9E9E9E') + '22', color: CARD_COLORS[t] || '#9E9E9E', fontWeight: 600, fontSize: 10 }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {u.primeiro_token ? new Date(u.primeiro_token).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color={u.ultimo_uso ? 'text.primary' : 'text.secondary'}>
                        {u.ultimo_uso ? new Date(u.ultimo_uso).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={tokenUsersTotal}
          page={tokenUsersPage}
          onPageChange={(_, p) => setTokenUsersPage(p)}
          rowsPerPage={tokenUsersRowsPerPage}
          onRowsPerPageChange={e => { setTokenUsersRowsPerPage(parseInt(e.target.value, 10)); setTokenUsersPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>
    </Box>
  );

  // ── Tab 6: Registos de tokenização (cada tentativa de guardar cartão) ──
  const ENROLL_STATUS_META: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default' }> = {
    COMPLETED: { label: 'Concluído', color: 'success' },
    FAILED:    { label: 'Falhado',   color: 'error' },
    CANCELLED: { label: 'Cancelado', color: 'warning' },
    PENDING:   { label: 'Pendente',  color: 'default' },
  };

  const renderEnrollments = () => (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {Object.entries(ENROLL_STATUS_META).map(([k, meta]) => (
          <Chip
            key={k}
            label={`${meta.label}: ${enrollPorEstado[k] || 0}`}
            color={meta.color}
            variant={enrollStatus === k ? 'filled' : 'outlined'}
            onClick={() => { setEnrollStatus(enrollStatus === k ? '' : k); setEnrollPage(0); }}
            size="small"
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Pesquisar por email, username ou referência…"
          value={enrollSearch}
          onChange={e => { setEnrollSearch(e.target.value); setEnrollPage(0); }}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') loadEnrollments(); }}
          sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      </Box>

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        {enrollLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Utilizador</TableCell>
                  <TableCell>Referência</TableCell>
                  <TableCell align="center">POS</TableCell>
                  <TableCell align="center">Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhum registo de tokenização encontrado
                    </TableCell>
                  </TableRow>
                ) : enrollments.map(r => (
                  <TableRow key={r.merchantRef} hover>
                    <TableCell>
                      <Typography variant="caption">
                        {r.criadoEm ? new Date(r.criadoEm).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', dateStyle: 'short', timeStyle: 'short' }) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{r.email || r.username || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.merchantRef}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" color="text.secondary">{r.posId || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={(ENROLL_STATUS_META[r.status] || { label: r.status }).label}
                        color={(ENROLL_STATUS_META[r.status] || { color: 'default' as const }).color}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          component="div"
          count={enrollTotal}
          page={enrollPage}
          onPageChange={(_, p) => setEnrollPage(p)}
          rowsPerPage={enrollRowsPerPage}
          onRowsPerPageChange={e => { setEnrollRowsPerPage(parseInt(e.target.value, 10)); setEnrollPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Card>
    </Box>
  );

  const renderTopups = () => (
    <Box>
      {topupsSummary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Carregamentos</Typography>
                <Typography variant="h5" fontWeight={700}>{fmtN(topupsSummary.count_topups)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Total Creditado (líquido)</Typography>
                <Typography variant="h5" fontWeight={700}>{fmt(topupsSummary.total_creditado)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Typography variant="body2" color="text.secondary">Total Taxas Cobradas</Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">{fmt(topupsSummary.total_taxas)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/*
        PAGOS E POR CREDITAR — dinheiro do cliente retido.

        O cartão é cobrado antes de creditar, de propósito, para que uma
        repetição não cobre duas vezes. Se o crédito falhar a seguir, o cliente
        fica pago e sem saldo, e a cotação fica parada sem que ninguém dê por
        ela. Havia uma assim na sandbox desde 14/08, calada durante meses.

        Só aparece quando há alguma: uma linha permanente a dizer «0» ensina a
        não olhar para ela.
      */}
      {!!topupsSummary?.por_creditar?.count && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => loadTopups()}>
              Reverificar
            </Button>
          }
        >
          <AlertTitle>
            {topupsSummary.por_creditar.count === 1
              ? '1 carregamento pago e por creditar'
              : `${topupsSummary.por_creditar.count} carregamentos pagos e por creditar`}
          </AlertTitle>
          {fmt(topupsSummary.por_creditar.total_cve)} cobrados ao cartão sem que a
          carteira tenha sido creditada
          {topupsSummary.por_creditar.mais_antiga
            ? ` — o mais antigo de ${new Date(topupsSummary.por_creditar.mais_antiga).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}`
            : ''}
          . Confirmar na SISP antes de creditar à mão: a cobrança pode ter sido
          estornada do lado deles.
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small" placeholder="Pesquisar por nome ou e-mail"
          value={topupsSearch}
          onChange={e => { setTopupsSearch(e.target.value); setTopupsPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 320 }}
        />
      </Box>

      {topupsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell>Utilizador</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="right">Cobrado</TableCell>
                <TableCell align="right">Creditado</TableCell>
                <TableCell align="right" sx={{ color: 'success.main' }}>Taxa</TableCell>
                <TableCell>Instrumento</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topups.map(t => (
                <TableRow key={t.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{t.nome}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{new Date(t.created_at).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}</Typography>
                  </TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={600}>{fmt(t.valor_bruto)}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2">{fmt(t.valor_creditado)}</Typography></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={t.fee_cve > 0 ? 'success.main' : 'text.secondary'} fontWeight={t.fee_cve > 0 ? 600 : 400}>
                      {t.fee_cve > 0 ? fmt(t.fee_cve) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip label={t.instrument} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Chip
                      label={t.status}
                      size="small"
                      color={t.status === 'COMPLETED' ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {topups.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sem carregamentos no período</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        component="div"
        count={topupsTotal}
        page={topupsPage}
        onPageChange={(_, p) => setTopupsPage(p)}
        rowsPerPage={topupsRowsPerPage}
        onRowsPerPageChange={e => { setTopupsRowsPerPage(parseInt(e.target.value, 10)); setTopupsPage(0); }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWallet color="primary" />
          <Typography variant="h5" fontWeight={700}>Wallet SISP — Estatísticas</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            select size="small" value={period} onChange={e => setPeriod(e.target.value as ReportPeriod)}
            sx={{ minWidth: 180 }}
          >
            {periodOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Tooltip title="Actualizar">
            <span>
              <AccountBalanceWallet sx={{ cursor: 'pointer', color: 'text.secondary' }} onClick={load} />
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Visão Geral" />
            <Tab label="Actividade" />
            <Tab label="Tokens / Cartões" />
            <Tab label="Utilizadores c/ Wallet" />
            <Tab label="Utilizadores c/ Token" />
            <Tab label="Carregamentos" />
            <Tab label="Registos de Cartões" />
          </Tabs>

          {tab === 0 && renderOverview()}
          {tab === 1 && renderActivity()}
          {tab === 2 && renderTokens()}
          {tab === 3 && renderUsers()}
          {tab === 4 && renderTokenUsers()}
          {tab === 5 && renderTopups()}
          {tab === 6 && renderEnrollments()}
        </>
      )}
    </Box>
  );
};

export default WalletStats;
